import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from "@angular/core";
import {FormControl, ValidationErrors, Validators} from "@angular/forms";
import {MeterStream, MeterType} from "@app/graphql-typings/graphql";
import {SiteId} from "@app/shared/services/api/access-sites.interface";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {concat, concatMap, EMPTY, from, Observable, of, reduce, Subject, switchMap, timer} from "rxjs";
import {IntervalDurationType, PeriodUtilService} from "@shared/services/util/period-util.service";
import {DateSelectComponentFields, DateTimeRange} from "@shared/components/date-time-range-select/date-time-range-select.component";
import {finalize, ignoreElements, map, takeUntil, takeWhile} from "rxjs/operators";
import {EstimatedMeterData, MeterDataEstimationResult, MeterProviderService, MeterStreamHistoryRange} from "@shared/services/api/meter-provider.service";
import moment, {Moment} from 'moment-timezone';
import {MeterdataType, MeterIdStreamId} from "@shared/services/api/meter-provider.interface";
import {TableColumn} from "@shared/components/ace-table/ace-table.component";
import {AceValidators} from "@shared/util/validators/ace-validators";
import {AppConstants} from "@shared/util/app-constants";
import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap/modal/modal-ref";
import {MetersCache} from "@shared/data-stores/meters-cache";
import {SiteService} from "@shared/services";
import {I18nMessage} from "@shared/api/model/i18n";
import {ConfirmationComponent, UIMessageType} from "@shared/components/confirmation/confirmation.component";
import {NgClass} from "@angular/common";
import {DurationTuple} from "@shared/api/model/timestamp";
import {DataInterval} from "@app/layout/sitemanager/meters/meters.service";
import {ButtonSize, ButtonVisual} from "../button/button.component";
import {ArrayUtils} from "@shared/util/collections/collection-utils";
import {FavouritesService} from "@app/layout/sitemanager/collectors-favourites/favourites.service";
import {AceLoggerService} from "@logging/ace-logger.service";
import {AceLogger} from "@logging/ace-logger";


interface AccumulatedMeterDataEstimationResult extends Omit<MeterDataEstimationResult, 'gaps'> {
  gapsByDataType: Map<MeterdataType, EstimatedMeterData>;
}


@Component({
  selector: 'app-estimate-meter-data-button',
  templateUrl: './estimate-meter-data-button.component.html',
  styleUrls: ['./estimate-meter-data-button.component.scss'],
  standalone: false
})
export class EstimateMeterDataButtonComponent implements OnInit, OnChanges, OnDestroy {
  private readonly logger: AceLogger;
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly MAX_ALLOWED_MONTHS = 3;
  private readonly DISABLED_NO_INTERVAL_MESSAGE: I18nMessage = {
    messageKey: 'Cannot estimate meter data for meters without data interval'
  };
  private readonly DISABLED_INTERVAL_NOT_SUPPORTED_MESSAGE: I18nMessage = {
    messageKey: 'Cannot estimate meter data for meters with data interval X'
  };
  private readonly DISABLED_NO_HISTORY_MESSAGE: I18nMessage = {
    messageKey: 'Cannot estimate meter data as the meter stream has no data'
  };
  private readonly DISABLED_NO_ACTIVE_SITE_MESSAGE: I18nMessage = {
    messageKey: 'No active site associated with current meter'
  };
  private readonly ESTIMATION_WINDOW_LIMIT: DurationTuple = {
    amount: 10,
    unit: 'day'
  };
  private readonly ESTIMATION_WINDOW_DELAY_MS = 10000;
  private readonly METER_DATA_HISTORY_ASYNC_VALIDATION_DEBOUNCE_MS = 1000;
  private readonly ONE_MINUTE_MS = 60 * 1000;
  private readonly ONE_HOUR_MS = 60 * this.ONE_MINUTE_MS;
  private readonly ONE_DAY_MS = 24 * this.ONE_HOUR_MS;
  private readonly STREAM_DATA_TYPE_FLAGS = new Set<string>(['B', 'E', 'K', 'Q']);

  @Input() siteId: SiteId;
  @Input() meterStream: MeterStream;
  @Input() showText = true;
  @Input() buttonStyles: NgClass['ngClass'];
  @Input() size: ButtonSize;
  @Input() visual: ButtonVisual;

  @ViewChild('estimateRangePicker', {static: true})
  estimateRangePicker: TemplateRef<any>;

  @ViewChild('estimatedMeterData', {static: true})
  estimatedMeterData: TemplateRef<any>;

  @ViewChild('headerTemplate', {static: true})
  headerTemplate: TemplateRef<any>;

  @ViewChild('cellTemplate', {static: true})
  cellTemplate: TemplateRef<any>;

  @ViewChild('translatedCellTemplate', {static: true})
  translatedCellTemplate: TemplateRef<any>;

  readonly noMeterGapsEstimatedMessage = {
    messageKey: 'No meter data estimated'
  };

  timezone: string;
  disabled = false;
  showTime = true;
  loading = false;
  estimating = false;
  disabledMessage: I18nMessage | null;

  durationTuple: DurationTuple;
  detectedDataPeriodStr: string | null = null;
  earliestHistoryDateTime: Moment;

  dateTimeRangeControl: FormControl<DateTimeRange>;

  estimatedMeterDataRanges: EstimatedMeterData[];
  meterDataGapColumns: TableColumn[];

  private modalRef: NgbModalRef;
  private dataTypeCount: number = null;

  constructor(private meterProviderService: MeterProviderService,
              private periodUtilService: PeriodUtilService,
              private siteService: SiteService,
              private metersCache: MetersCache,
              private modalService: NgbModal,
              private favouriteService: FavouritesService,
              logService: AceLoggerService) {
    this.logger = logService.create(EstimateMeterDataButtonComponent.name);
  }

  ngOnInit(): void {
    this.dateTimeRangeControl = new FormControl<DateTimeRange>(null, {
      validators: [Validators.required, this.estimateRangeValidator],
      asyncValidators: [this.meterDataOverlapAdjacencyAsyncValidator]
    });
    this.meterDataGapColumns = [{
      name: {messageKey: 'Meter Data Type'},
      headerTemplate: this.headerTemplate,
      cellTemplate: this.translatedCellTemplate,
      prop: 'data_type'
    }, {
      name: {messageKey: 'Number of Entries'},
      headerTemplate: this.headerTemplate,
      cellTemplate: this.cellTemplate,
      prop: 'count'
    }];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.meterStream || changes.siteId) {
      if (this.meterStream == null) {
        throw new Error('Estimate Meter Data Button cannot function without meter stream.');
      }
      this.dataTypeCount = null;
      const getStreamMeter$ = this.meterStream.nmi != null
        ? of(this.meterStream.nmi)
        : this.metersCache.getMetersBySiteIds([this.siteId]).pipe(map(metersById => metersById.get(this.meterStream.nmi_id)));
      getStreamMeter$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(meter => {
        this.meterStream.nmi = meter;
        this.updateButtonDisabledState();
        if (!this.disabled) {
          this.durationTuple = this.periodUtilService.intervalToDurationTuple(meter.data_interval);
          this.showTime = this.periodUtilService.getIntervalDurationType(meter.data_interval) === IntervalDurationType.PT;
          if (meter.site_id != null) {
            this.siteService.getCachedSiteInfo(meter.site_id).pipe(takeUntil(this.ngUnsubscribe)).subscribe(siteInfo => {
              if (siteInfo != null) {
                this.timezone = siteInfo.timezone;
              } else {
                this.disabled = true;
                this.disabledMessage = this.DISABLED_NO_ACTIVE_SITE_MESSAGE;
              }
            });
          } else {
            this.disabled = true;
            this.disabledMessage = this.DISABLED_NO_ACTIVE_SITE_MESSAGE;
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  estimateMeterData(forcedDataPeriod: string | null): void {
    this.estimating = true;
    const estimateMeterDataFn = ([startTs, endTs]: [Moment, Moment]) =>
      this.meterProviderService.estimateMeterData([this.meterIdStreamId], [{start_ts: startTs.toISOString(), end_ts: endTs.toISOString()}], forcedDataPeriod);
    this.getEstimateWindows().pipe(
      switchMap(estimateWindows =>
        from(estimateWindows).pipe(
          concatMap((estimateWindow, index) =>
            concat(
              estimateMeterDataFn(estimateWindow),
              index === estimateWindows.length - 1
                ? EMPTY
                : timer(this.ESTIMATION_WINDOW_DELAY_MS).pipe(ignoreElements()) // Delays next emit but won't delay the reduce from running
            )
          ),
          // Stops further emits after the first time `estimationResult.detectedDataPeriod` is not `null`
          takeWhile(estimationResult => estimationResult.detectedDataPeriod == null, true),
          reduce((finalEstimationResult, estimationResult) => {
            finalEstimationResult.detectedDataPeriod ??= estimationResult.detectedDataPeriod;
            finalEstimationResult.error ??= estimationResult.error;
            estimationResult.gaps?.forEach(estimatedMeterData => {
              const meterDataType = estimatedMeterData.data_type;
              const dataTypeGap = finalEstimationResult.gapsByDataType.get(meterDataType);
              if (dataTypeGap) {
                dataTypeGap.end_ts = moment.max(dataTypeGap.end_ts, estimatedMeterData.end_ts);
                dataTypeGap.count += estimatedMeterData.count;
              } else {
                finalEstimationResult.gapsByDataType.set(meterDataType, estimatedMeterData);
              }
            });
            return finalEstimationResult;
          }, <AccumulatedMeterDataEstimationResult>{
            gapsByDataType: new Map<MeterdataType, EstimatedMeterData>()
          })
        )),
      finalize(() => this.estimating = false)
    ).subscribe(result => {
      if (result.detectedDataPeriod) {
        this.detectedDataPeriodStr = result.detectedDataPeriod;
      } else if (result.gapsByDataType != null) {
        this.estimatedMeterDataRanges = Array.from(result.gapsByDataType.values());
        this.modalRef?.close();
        this.modalRef = this.modalService.open(this.estimatedMeterData, {size: 'xl'});
      } else {
        ConfirmationComponent.createModal(this.modalService, {
          title: {messageKey: 'Error when estimating meter data'},
          messages: [{
            text: result.error,
            type: UIMessageType.DANGER
          }],
          hideCancelButton: true
        });
      }
    });
  }

  checkAndShowEstimateRangePicker(): void {
    if (!this.earliestHistoryDateTime) {
      this.loading = true;
      this.meterProviderService.getMeterDataHistoryRangeForMeterStream(this.meterIdStreamId)
        .pipe(finalize(() => this.loading = false), takeUntil(this.ngUnsubscribe))
        .subscribe((meterDataHistoryRange: MeterStreamHistoryRange) => {
          this.earliestHistoryDateTime = meterDataHistoryRange?.start_ts ?? null;
          this.meterStream.history_available = !!this.earliestHistoryDateTime;
          if (this.earliestHistoryDateTime) {
            this.showEstimateRangePicker();
          } else {
            this.disabled = true;
            this.disabledMessage = this.DISABLED_NO_HISTORY_MESSAGE;
            ConfirmationComponent.createModal(this.modalService, {
              title: {messageKey: 'No meter data available'},
              body: this.disabledMessage,
              yesText: {messageKey: 'OK'},
              hideCancelButton: true
            });
          }
        });
    } else {
      this.showEstimateRangePicker();
    }
  }

  private updateButtonDisabledState(): void {
    this.disabled = false;
    if (this.meterStream.history_available === false) {
      // if history_available is undefined or null, the history will be checked upon pressing the button
      this.disabled = true;
      this.disabledMessage = this.DISABLED_NO_HISTORY_MESSAGE;
    } else {
      const dataInterval = this.meterStream.nmi?.data_interval ?? null;
      if (!dataInterval) {
        this.disabled = true;
        this.disabledMessage = this.DISABLED_NO_INTERVAL_MESSAGE;
      } else if (!this.intervalSupported(dataInterval)) {
        this.disabled = true;
        this.disabledMessage = {
          ...this.DISABLED_INTERVAL_NOT_SUPPORTED_MESSAGE,
          parameters: {
            dataInterval: DataInterval.get(dataInterval).valueOf()
          }
        };
      }
    }
  }

  private showEstimateRangePicker(): void {
    this.modalRef?.close();
    this.modalRef = this.modalService.open(this.estimateRangePicker, {size: 'lg'});
  }

  formatDataPeriod(dataPeriod: string): string {
    const duration = this.periodUtilService.toDuration(dataPeriod);
    return this.formatDuration(duration);
  }

  formatTimestamp(ts: Moment): string {
    return moment.tz(ts, this.timezone).format(this.showTime ? AppConstants.TIME_FORMAT_SECOND_PRECISION : AppConstants.DATE_FORMAT);
  }

  private get meterIdStreamId(): MeterIdStreamId {
    return {nmi_id: this.meterStream.nmi_id, stream_id: this.meterStream.stream_id};
  }

  private intervalSupported(interval: string): boolean {
    const intervalMs = this.periodUtilService.intervalToMs(interval);
    return (this.withinHourInterval(intervalMs) && this.factorOfInterval(intervalMs, this.ONE_HOUR_MS))
      || (this.withinDayInterval(intervalMs) && this.factorOfInterval(intervalMs, this.ONE_DAY_MS));
  }

  private withinHourInterval(intervalMs: number): boolean {
    return intervalMs > this.ONE_MINUTE_MS && intervalMs <= this.ONE_HOUR_MS;
  }

  private withinDayInterval(intervalMs: number): boolean {
    return intervalMs > this.ONE_HOUR_MS && intervalMs <= this.ONE_DAY_MS;
  }

  private factorOfInterval(intervalMs: number, targetIntervalMs: number): boolean {
    return targetIntervalMs % intervalMs === 0;
  }

  // format duration to the best readable format, e.g., 360 mins => 6 hours
  private formatDuration(duration: moment.Duration): string {
    return this.periodUtilService.formatDuration(duration, true, 'minute');
  }

  private validateEstimationWindowTooWide(startTs: Moment, endTs: Moment): ValidationErrors | null {
    if (endTs.diff(startTs, 'month') > this.MAX_ALLOWED_MONTHS) {
      return {
        messageKey: 'Selected date range cannot span longer than x months',
        parameters: {months: this.MAX_ALLOWED_MONTHS}
      };
    }
    return null;
  }

  private estimateRangeValidator = (control: FormControl<DateTimeRange>): ValidationErrors | null => {
    const dateTimeRange = control.value;
    if (dateTimeRange != null) {
      const startTs = dateTimeRange[DateSelectComponentFields.START_DATE];
      const endTs = dateTimeRange[DateSelectComponentFields.END_DATE];
      const {amount: period, unit} = this.durationTuple;
      let error: ValidationErrors;

      if (!startTs.isAfter(this.earliestHistoryDateTime, unit)) {
        error = {
          messageKey: 'Start date must be later than earliest meter data history date time',
          parameters: {
            earliestMeterDataHistory: this.formatTimestamp(this.earliestHistoryDateTime)
          }
        }
      } else if (!startTs.isSameOrBefore(endTs.clone().subtract(period, unit), unit)) {
        error = {
          messageKey: 'Start date must be at least duration before end date',
          parameters: {duration: this.formatDuration(moment.duration(period, unit))}
        };
      } else {
        const windowTooWideError = this.validateEstimationWindowTooWide(startTs, endTs);
        if (windowTooWideError) {
          error = windowTooWideError;
        } else {
          const roundedStartTs = this.periodUtilService.roundDateTimeToPeriod(startTs, period, unit);
          const roundedEndTs = this.periodUtilService.roundDateTimeToPeriod(endTs, period, unit);
          const formattedDataPeriod = this.formatDuration(moment.duration(period, unit));
          if (!startTs.isSame(roundedStartTs, unit)) {
            error = {
              [DateSelectComponentFields.START_DATE]: {
                messageKey: 'Start date not aligned to data interval',
                parameters: {dataPeriod: formattedDataPeriod}
              }
            };
          }
          if (!endTs.isSame(roundedEndTs, unit)) {
            error = {
              ...error,
              [DateSelectComponentFields.END_DATE]: {
                messageKey: 'End date not aligned to data interval',
                parameters: {dataPeriod: formattedDataPeriod}
              }
            };
          }
        }
      }
      if (error != null) {
        return {invalidEstimationWindow: error};
      }
    }
    return null;
  };

  private meterDataOverlapAdjacencyAsyncValidator = AceValidators.customAsyncValidator({
    validationFn: (): Observable<ValidationErrors | null> => {
      const dateTimeRange = this.dateTimeRangeControl.value;
      if (dateTimeRange != null) {
        const startTs = dateTimeRange[DateSelectComponentFields.START_DATE];
        const endTs = dateTimeRange[DateSelectComponentFields.END_DATE];
        const {amount: period, unit} = this.durationTuple;
        // though we have run the validation in the sync validators, we want to be super defensive here just in case angular has a bug (or we misused)
        // where async validator is triggered when sync validators fail, we run it again here to be absolutely sure the window's not too wide
        // and if we ever see 2 same errors stack up, we know either angular has a bug or we misused it.
        const estimationWindowTooWideError = this.validateEstimationWindowTooWide(startTs, endTs);
        if (estimationWindowTooWideError != null) {
          return of({invalidEstimationWindow: estimationWindowTooWideError});
        } else {
          const searchCriteria = {
            meterIdsStreamIds: [this.meterIdStreamId],
            localStartTs: startTs.clone().subtract(period, unit),
            localEndTs: endTs.clone().add(period, unit)
          };
          return this.meterProviderService.getMeterData(searchCriteria).pipe(takeUntil(this.ngUnsubscribe), map(meterdata => {
            if (!meterdata?.length) {
              return {
                invalidEstimationWindow: {
                  messageKey: 'Selected date range must overlap or be next to existing meter data'
                }
              };
            }
            return null;
          }));
        }
      }
      return of(null);
    },
    validationWaitMs: this.METER_DATA_HISTORY_ASYNC_VALIDATION_DEBOUNCE_MS
  });

  private getDataTypeFlag(favIdentifier: string): string | null {
    const favIdentifiersSplit = favIdentifier.split('-');
    return favIdentifiersSplit[favIdentifiersSplit.length - 3]?.[0] ?? null;
  }

  private resolveDataTypeCount(): Observable<number> {
    if (this.dataTypeCount != null) {
      return of(this.dataTypeCount);
    }
    return this.favouriteService.getFavouriteIdentifiers({canonical_equipment_id: this.meterStream.equipment_id}).pipe(map(favourites => {
      const dataTypeFlags = new Set<string>();
      favourites.forEach(fav => {
        const dataType = this.getDataTypeFlag(fav.identifier);
        if (this.STREAM_DATA_TYPE_FLAGS.has(dataType)) {
          dataTypeFlags.add(dataType);
        } else {
          this.logger.warn(`Cannot get stream data type flag from favourite identifier: fav-id=[${fav.fav_id}], identifier=[${fav.identifier}]`);
        }
      });
      this.dataTypeCount = dataTypeFlags.size || (this.meterStream.nmi.meter_type === MeterType.NMI ? 4 : 1);
      return this.dataTypeCount;
    }));
  }

  private getEstimateWindows(): Observable<[Moment, Moment][]> {
    const dateTimeRange = this.dateTimeRangeControl.value;
    const startTs = dateTimeRange[DateSelectComponentFields.START_DATE];
    const endTs = dateTimeRange[DateSelectComponentFields.END_DATE];
    // backend throttles data estimation, there would be a pause of 1 minute for every 10 data-type day,
    // so here we chunk up the request to be exactly that to avoid being throttled
    return this.resolveDataTypeCount().pipe(map(dataTypeCount => {
      const estimateWindowSize = Math.max(Math.floor(this.ESTIMATION_WINDOW_LIMIT.amount / dataTypeCount), 1);
      const estimateSteps: Moment[] = this.periodUtilService.generateTimestampSequence(
        startTs, endTs, estimateWindowSize, this.ESTIMATION_WINDOW_LIMIT.unit, {startInclusive: true, endInclusive: true});
      return <[Moment, Moment][]>ArrayUtils.partition(estimateSteps, 2, 1);
    }));
  }
}
